/** @typedef { import('rxjs') } Observable */
import axios from 'axios';
import { get } from 'lodash-es';
import moment from 'moment';
import { from } from 'rxjs';

export const validate2 = (fulfillmentPlanIds = []) => {
  const post = axios
    .post('/bulk/validate2', {
      fulfillmentPlanId: fulfillmentPlanIds.map((fp) => parseInt(fp, 10)),
    })
    .then((response) => {
      return get(response, 'data');
    });

  const post$ = from(post);
  return post$;
};

export const canPurchaseLabel = (fulfillmentPlanIds = []) => {
  const post = axios.post('/label/canPurchaseForFulfillmentPlans', fulfillmentPlanIds).then((response) => {
    return get(response, 'data');
  });

  const post$ = from(post);
  return post$;
};

/**
 * @typedef {object} BulkCostReviewRequest
 * @property {string[] | number[]} [fulfillmentPlanId] - FulfillmentPlanId filter (is actually array of fp ids, not singular)
 * @property {string} [fulfillmentPlanSetId]
 * @property {string[]} [excludedFulfillmentPlanId]
 * @property {boolean} [isLabelCreated]
 */

/**
 * @typedef {object} BulkCostReviewResponse
 * @property {string[]} jobGuids
 */

/**
 * @param {BulkCostReviewRequest} request
 * @param {boolean} [hasAcceptedUpsLabelTerms]
 * @returns {Promise<axios.AxiosResponse<BulkCostReviewResponse>>}
 */
export const bulkCostReview = (request, hasAcceptedUpsLabelTerms) => {
  if (hasAcceptedUpsLabelTerms != null) {
    return axios.post('/bulk/costreview', request, { params: { hasAcceptedUpsLabelTerms } });
  }
  return axios.post('/bulk/costreview', request);
};

/**
 * @param {string[]} fulfillmentPlanIds
 * @param {boolean} [hasAcceptedUpsLabelTerms]
 * @returns {import('rxjs').Observable}
 */
export const costReview = (fulfillmentPlanIds = [], hasAcceptedUpsLabelTerms) => {
  const post = bulkCostReview(
    {
      fulfillmentPlanId: fulfillmentPlanIds.map((fp) => parseInt(fp, 10)),
    },
    hasAcceptedUpsLabelTerms,
  ).then((response) => {
    return get(response, 'data');
  });

  const post$ = from(post);
  return post$;
};

export const getLabelPrintedStatusOfFulfillmentPlans = (fulfillmentPlanIds = []) => {
  const postAndTransform = axios
    .post('/fulfillment/labelprintedstatus/fulfillmentplan', fulfillmentPlanIds)
    .then((response) => response.data);

  const request$ = from(postAndTransform);
  return request$;
};

/**
 * @template T
 * @typedef {import('rxjs').Observable<T>} CreateLabel$
 * @param {string[]} fulfillmentPlanIds
 * @param {Date} shipDate
 * @returns {CreateLabel$<{ labelBatchIds: number[] }>}
 */
export const createLabel = (fulfillmentPlanIds = [], shipDate = moment().utc().format()) => {
  const post = axios
    .post('/bulk/createlabel2', {
      filter: {
        fulfillmentPlanId: fulfillmentPlanIds.map((fp) => parseInt(fp, 10)),
      },
      shipDate,
    })
    .then((res) => res.data);

  const request$ = from(post);
  return request$;
};

export const createExternalFulfillment = (fulfillmentPlanIds = []) => {
  const post = axios
    .post('bulk/sendfulfillmentrequest', {
      fulfillmentPlanId: fulfillmentPlanIds.map((fp) => parseInt(fp, 10)),
    })
    .then((res) => res.data);

  const request$ = from(post);
  return request$;
};

export const fulfillmentPlanToFulfillment = (fulfillmentPlanId = []) => {
  const post = axios.post('/label/status/byFulfillmentPlanWithReturns', { fulfillmentPlanId }).then((response) => response.data);

  const post$ = from(post);
  return post$;
};

/**
 * @template T
 * @typedef {import('rxjs').Observable<T>} GetLabelErrors$
 * @param {string[]} labelBatchIds
 * @returns {GetLabelErrors$<{ labelBatchid: string, items: { fulfillmentPlanId: string, status: string, errorMessage: string, errorCode: string, errorField: string }[] }[]>}
 */
export const getBulkCreateLabelErrors = (labelBatchIds = []) => {
  const post = axios.post('/bulk/createlabel/status', { labelBatchIds }).then((response) => response.data);

  const post$ = from(post);
  return post$;
};

export const restoreBulkShipments = (fulfillmentPlanIdObj) => {
  return axios.post('bulk/restore', fulfillmentPlanIdObj);
};

/**
 * @typedef {object} BulkRateFulfillmentPlansResponse
 * @property {string[]} rateJobGuids - The list of rate job GUIDs.
 * @property {string[]} alternativeRateJobGuids - The list of alternative rate job GUIDs.
 */

/**
 * @param {object} filters
 * @param {string|undefined} filters.orderGridStatus - The order grid status to get the summary for.
 * @param {string[]|undefined} filters.fulfillmentPlanIds - The list of fulfillment plan ids to get the summary for.
 * @param {string[]|undefined} filters.fulfillmentPlanSetId - The list of batches to get the summary for.
 * @param {string|undefined} filters.searchTerm - The search term to get the summary for.
 * @param {string|undefined} filters.storeGuid - The store ID to get the summary for.
 * @param {object[]|undefined} filters.collectionFilters - The collection filters to get the summary for.
 * @param {boolean|undefined} filters.forceRefreshRate - Force rating even for fulfillment plans that already have rates.
 * @returns {Promise<AxiosResponse<BulkRateFulfillmentPlansResponse>>}
 */
export const bulkRateFulfillmentPlans = (filters) => {
  return axios.post('/bulk/bulkRateFulfillmentPlans', filters).then((response) => response?.data);
};

export const bulkRateFulfillmentPlans$ = (filters) => {
  return from(bulkRateFulfillmentPlans(filters));
};

/**
 * @param {object} filter
 * @returns {Promise<AxiosResponse<BulkRateFulfillmentPlansResponse>>}
 */
export const bulkRateSummary = (filter) => {
  return axios.post('/bulk/bulkRateSummary', filter).then((response) => response?.data);
};
